import { DeleteOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Col, Form, type FormInstance, Popconfirm, Row } from 'antd';
import { useEffect, useState } from 'react';
import type { ProdutoVariacao } from '@/features/produto-variacao/types/ProdutoVariacao';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type {
    ProdutoBodyVariacao,
    ProdutoBodyVariacaoGrade,
    ProdutoBodyVariacaoVariacao,
} from '@/features/produto/types/ProdutoBody';
import { Container } from '@/components/Container';
import { InputTag, type InputTagValue } from '@/components/input/InputTag';
import { apiVariacaoConsultarQueryOptions } from '@/features/produto-variacao/api/apiVariacaoConsultar';
import { ProdutoVariacaoSelect } from '@/features/produto-variacao/components/ProdutoVariacaoSelect';
import { generateCombinations } from '@/features/produto/util/generateCombinations';

export function ProdutoTabVariacaoVariacoes({
    form,
    setFormDirty,
}: {
    form: FormInstance<FormProdutoValues>;
    setFormDirty: (dirty: boolean) => void;
}) {
    const variacaoForm = Form.useWatch('variacao', form);
    const nomeProduto = Form.useWatch('pro_descricao', form);

    const [showWarning, setShowWarning] = useState(false);
    const [variacaoSelecionada, setVariacaoSelecionada] = useState<ProdutoVariacao | null>(null);

    const { data } = useQuery(apiVariacaoConsultarQueryOptions({}));

    const adicionar = (): void => {
        if (!variacaoSelecionada) {
            return;
        }

        const variacaoForm = form.getFieldValue('variacao') as ProdutoBodyVariacao;
        const variacao = variacaoForm?.variacao || [];
        const grade = variacaoForm?.grade || [];
        let newVariacao: ProdutoBodyVariacaoVariacao[] = [];
        const found = variacao.find((v) => v.pve_variacao_idpk === variacaoSelecionada.var_idpk);

        if (found) {
            newVariacao = variacao.map((v) => {
                if (v.pve_variacao_idpk === variacaoSelecionada.var_idpk) {
                    v.excluir = undefined;
                    v.valor = v.valor
                        ?.filter((vv) => typeof vv.pvv_idpk === 'number')
                        .map((vv) => {
                            vv.excluir = 'S';
                            return vv;
                        });
                }

                return v;
            });
        } else {
            newVariacao = [
                ...variacao,
                {
                    pve_nome: variacaoSelecionada.var_nome,
                    pve_variacao_idpk: variacaoSelecionada.var_idpk,
                    valor: [],
                },
            ];
        }

        const newGrade = grade
            .filter((g) => g.pro_idpk !== null)
            .map((g) => {
                g.excluir = 'S';
                return g;
            });

        form.setFieldsValue({
            variacao: {
                grade: newGrade,
                variacao: newVariacao,
            },
        });

        setVariacaoSelecionada(null);
        setShowWarning(false);
        setFormDirty(true);
    };

    const recalcularGrade = (): void => {
        const variacaoForm = form.getFieldValue('variacao') as ProdutoBodyVariacao;
        const variacao = (variacaoForm.variacao || []).filter((v) => v.excluir !== 'S');

        // somente recalcular grades se todas as variações tiverem pelo menos um valor
        const todasVariacoesComValor = variacao.every(
            (v) => Array.isArray(v.valor) && v.valor.length > 0,
        );

        if (!todasVariacoesComValor) {
            return;
        }

        const combinations = generateCombinations(variacao);

        let grade = variacaoForm?.grade || [];
        const novasGrades: ProdutoBodyVariacaoGrade[] = [];

        for (const combination of combinations) {
            if (combination.length === 0) {
                continue;
            }

            const gradeAtual = `${nomeProduto} - ${combination.map((c) => c.pvv_valor).join(' - ')}`;

            const found = grade.find((g) => g.pro_descricao === gradeAtual);

            if (found) {
                grade = grade.map((g) => {
                    if (g.pro_descricao === gradeAtual) {
                        return { ...g, excluir: undefined };
                    }

                    return g;
                });
            } else {
                novasGrades.push({
                    estoque_total: 0,
                    pro_cod_barra: '',
                    pro_codigo_interno: 0,
                    pro_descricao: gradeAtual,
                    pro_idpk: undefined,
                    pro_marcadores: '',
                    pro_preco_custo: 0,
                    pro_preco_venda: 0,
                    pro_sku: '',
                    variacao_valor: combination,
                });
            }
        }

        const newGrade = [...grade, ...novasGrades];
        newGrade.sort((a, b) => a.pro_descricao.localeCompare(b.pro_descricao));

        form.setFieldValue('variacao', {
            ...variacaoForm,
            grade: newGrade,
        });

        setFormDirty(true);
    };

    const onChangeInputTag = (variacao: ProdutoBodyVariacaoVariacao, e: InputTagValue[]): void => {
        let excluir = false;

        const newVariacao = variacaoForm?.variacao?.map((v) => {
            if (v.pve_variacao_idpk === variacao.pve_variacao_idpk) {
                const newValor = e.map((val) => ({
                    excluir: val.excluir,
                    pvv_idpk: val.idpk,
                    pvv_valor: val.label,
                }));

                excluir = newValor.every((nv) => nv.excluir === 'S');

                return {
                    ...v,
                    excluir: excluir ? 'S' : undefined,
                    valor: newValor,
                };
            }

            return v;
        });

        form.setFieldValue('variacao', {
            ...variacaoForm,
            variacao: newVariacao,
        });

        setFormDirty(true);
        recalcularGrade();
    };

    const onTagRemove = (variacaoRemovida: string): void => {
        const variacao = form.getFieldValue('variacao') as ProdutoBodyVariacao;
        const gradeForm = variacao?.grade || [];
        const newGrade: ProdutoBodyVariacaoGrade[] = [];

        for (const grade of gradeForm) {
            // Se tem pro_idpk e tem a tag mandar excluir
            const variacoes = grade.pro_descricao.split(' - ');
            // remove o nome do produto, deixando apenas as variações
            variacoes.shift();

            const temVariacao = variacoes.includes(variacaoRemovida);

            // Produto não foi cadastrado
            if (grade.pro_idpk === null) {
                if (temVariacao) {
                    // se tem a variação não adiciona na nova lista
                } else {
                    // se não tem a variação adiciona na nova lista
                    newGrade.push(grade);
                }

                continue;
            }

            // Tem produto cadastrado

            if (temVariacao) {
                grade.excluir = 'S';
            }

            newGrade.push(grade);
        }

        form.setFieldValue('variacao', { ...variacao, grade: newGrade });
        setFormDirty(true);
    };

    const removerVariacao = (pve_variacao_idpk: number): void => {
        const variacaoForm = form.getFieldValue('variacao') as ProdutoBodyVariacao;
        const variacao = variacaoForm?.variacao || [];
        const grade = variacaoForm?.grade || [];

        const newVariacao = variacao.map((v) => {
            if (v.pve_variacao_idpk === pve_variacao_idpk) {
                return { ...v, excluir: 'S' as const };
            }

            return v;
        });

        const newGrade = grade
            .filter((g) => g.pro_idpk !== null)
            .map((g) => ({ ...g, excluir: 'S' as const }));

        form.setFieldValue('variacao', {
            ...variacaoForm,
            grade: newGrade,
            variacao: newVariacao,
        });

        setFormDirty(true);
        recalcularGrade();
    };

    useEffect(() => {
        if (!nomeProduto) {
            return;
        }

        const variacaoForm = form.getFieldValue('variacao') as ProdutoBodyVariacao;
        const grade = variacaoForm?.grade || [];

        const newGrade = grade.map((g) => {
            const arrDescricao = g.pro_descricao.split(' - ');
            arrDescricao[0] = nomeProduto;
            g.pro_descricao = arrDescricao.join(' - ');
            return g;
        });

        form.setFieldValue('variacao', { ...variacaoForm, grade: newGrade });
    }, [nomeProduto, form]);

    return (
        <Container title='Variações do produto'>
            <Row gutter={[8, 8]} style={{ marginBottom: 10 }}>
                <Col lg={4} md={8} sm={12} xs={24}>
                    <ProdutoVariacaoSelect
                        filter={variacaoForm?.variacao
                            ?.filter((v) => v.excluir !== 'S')
                            .map((v) => v.pve_nome || '')}
                        onChange={(_, vari) => setVariacaoSelecionada(vari)}
                        value={variacaoSelecionada?.var_idpk}
                    />
                </Col>

                <Button
                    disabled={!(variacaoSelecionada && nomeProduto)}
                    onClick={() => {
                        if (Array.isArray(variacaoForm?.grade) && variacaoForm.grade.length > 0) {
                            setShowWarning(true);
                        } else {
                            adicionar();
                        }
                    }}
                    type='primary'
                >
                    Adicionar variação
                </Button>

                <Button onClick={recalcularGrade} style={{ marginLeft: 10 }} type='primary'>
                    Recalcular grades
                </Button>
            </Row>

            {showWarning && (
                <Row gutter={[8, 8]}>
                    <Col md={8} sm={12} xs={24}>
                        <Alert
                            description={
                                <>
                                    <h4>Atenção</h4>
                                    Ao adicionar uma nova variação, as grades existentes serão
                                    excluídas. Você tem certeza que deseja continuar?
                                    <Row>
                                        <Button
                                            onClick={() => setShowWarning(false)}
                                            style={{ marginRight: 10 }}
                                        >
                                            Não
                                        </Button>
                                        <Button onClick={adicionar} type='primary'>
                                            Sim
                                        </Button>
                                    </Row>
                                </>
                            }
                            showIcon={true}
                            type='warning'
                        />
                    </Col>
                </Row>
            )}

            {variacaoForm?.variacao
                ?.filter((variacao) => variacao.excluir !== 'S')
                .map((variacao) => {
                    const variacaoData = data?.registros.find(
                        (d) => d.var_idpk === variacao.pve_variacao_idpk,
                    );

                    return (
                        <div key={variacao.pve_idpk} style={{ marginBottom: 10 }}>
                            <p className='m-0 font-bold'>{variacao.pve_nome}</p>
                            <p style={{ margin: 0 }}>{variacaoData?.var_descricao || ''}</p>
                            <Row gutter={[8, 8]}>
                                <Col md={8} sm={12} xs={24}>
                                    <InputTag
                                        onChange={(e) => onChangeInputTag(variacao, e)}
                                        onTagRemove={onTagRemove}
                                        tags={variacao.valor?.map((valor) => ({
                                            closable: true,
                                            color: variacaoData?.var_cor ?? '',
                                            excluir: valor.excluir,
                                            idpk: valor.pvv_idpk,
                                            label: valor.pvv_valor || '',
                                            value: valor.pvv_valor || '',
                                        }))}
                                    />
                                </Col>

                                <Popconfirm
                                    onConfirm={() => {
                                        if (variacao.pve_variacao_idpk) {
                                            removerVariacao(variacao.pve_variacao_idpk);
                                        }
                                    }}
                                    title='Tem certeza que deseja excluir esta variação?'
                                >
                                    <DeleteOutlined style={{ color: 'red' }} />
                                </Popconfirm>
                            </Row>
                        </div>
                    );
                })}
        </Container>
    );
}
